import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Loader2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const GENDERS = [
  { v: "women", l: "Women" },
  { v: "men", l: "Men" },
  { v: "unisex", l: "Unisex" },
];
const STYLES = [
  { v: "trendy", l: "Trendy" },
  { v: "classic", l: "Classic" },
  { v: "professional", l: "Professional" },
  { v: "luxury", l: "Luxury" },
];

export default function Analyze() {
  const { user, refresh } = useAuth();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gender, setGender] = useState("women");
  const [style, setStyle] = useState("trendy");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onPick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Please upload a JPEG, PNG or WEBP image.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) {
      toast.error("Please select a selfie first.");
      return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/analyze?gender_pref=${gender}&style_pref=${style}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Your reading is ready.");
      await refresh();
      navigate(`/analysis/${res.data.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Analysis failed. Please try again.";
      toast.error(msg);
      if (e?.response?.status === 402) navigate("/pricing");
    } finally {
      setLoading(false);
    }
  };

  const quotaLeft = user?.is_premium ? "Unlimited" : `${Math.max(0, 3 - (user?.free_analyses_used || 0))} of 3 left this month`;

  return (
    <main className="crown-container py-12 md:py-16" data-testid="analyze-page">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* LEFT: upload */}
        <section className="lg:col-span-7">
          <p className="font-overline mb-3">New consultation</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-3">Sit for your reading.</h1>
          <p className="text-ash mb-2">{quotaLeft}</p>
          <p className="text-ash text-sm mb-8">Look directly at the camera. Natural lighting. Hair pulled back if possible.</p>

          <label
            htmlFor="selfie-input"
            data-testid="selfie-dropzone"
            className="block aspect-[4/5] relative overflow-hidden border border-dashed border-bone/20 cursor-pointer hover:border-gold transition-colors group"
          >
            {preview ? (
              <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-obsidian/30">
                <Upload className="text-gold" strokeWidth={1} size={42} />
                <p className="font-overline text-bone">Drop or browse</p>
                <p className="text-xs text-ash">JPEG · PNG · WEBP · up to 8MB</p>
              </div>
            )}
            <input
              id="selfie-input"
              data-testid="selfie-input"
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
          </label>
        </section>

        {/* RIGHT: preferences */}
        <section className="lg:col-span-5 lg:pt-12">
          <p className="font-overline mb-3">Preferences</p>
          <h2 className="font-serif text-2xl mb-8">Calibrate your atelier.</h2>

          <div className="mb-10">
            <p className="font-overline text-ash mb-4">Style direction</p>
            <div className="grid grid-cols-3 gap-2">
              {GENDERS.map((g) => (
                <button
                  key={g.v}
                  data-testid={`gender-${g.v}`}
                  onClick={() => setGender(g.v)}
                  className={`px-4 py-3 font-overline text-xs border transition-colors ${
                    gender === g.v ? "border-gold text-gold bg-gold/5" : "border-bone/15 text-ash hover:text-bone"
                  }`}
                >
                  {g.l}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <p className="font-overline text-ash mb-4">Aesthetic</p>
            <div className="grid grid-cols-2 gap-2">
              {STYLES.map((s) => (
                <button
                  key={s.v}
                  data-testid={`style-${s.v}`}
                  onClick={() => setStyle(s.v)}
                  className={`px-4 py-3 font-overline text-xs border transition-colors ${
                    style === s.v ? "border-gold text-gold bg-gold/5" : "border-bone/15 text-ash hover:text-bone"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          <button onClick={submit} disabled={loading || !file} className="crown-btn-primary w-full justify-center" data-testid="analyze-submit-btn">
            {loading ? (<><Loader2 className="animate-spin" size={16} strokeWidth={1.5} /> Reading your features…</>) : (<>Reveal my recommendations <ArrowUpRight size={16} strokeWidth={1.5} /></>)}
          </button>

          <p className="font-overline text-ash mt-8">No selfies stored without consent · processed via Emergent secure storage</p>
        </section>
      </div>
    </main>
  );
}
